import React, { useState, useEffect, useMemo, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WindowLocation, useLocation as useLocationRouter } from '@reach/router';
import { isEqual } from 'lodash';

import MSAFilter from '../../MSAFilter/MSAFilter';
import Products from '../../sections/Products/Products';
import Seo from '../../Seo/Seo';
import LegalText from '../../UI/LegalText/LegalText';
import Phobio from '../../Phobio/Phobio';
import isKiosk from '../../../utils/isKiosk';
import extractArrayData from '../../../utils/extractArrayData';
import { Contentful } from '../../../types';
import useGetTradeInPhones from '../../../hooks/useGetTradeInPhones';
import {
  updateCurrentProvince,
  updateStoredFilters,
} from '../../../redux/reducers/filtersReducer';
import {
  filtersSelector,
  provinceSelector,
} from '../../../redux/selectors/filtersSelectors';
import useLocation from '../../../utils/location';
import navigateToURLWithQueryParams from '../../../utils/navigateToURLWithQueryParams';
import {
  ContentfulCombinatorSection, ContentfulConditionalSection, ContentfulForm, MobilistTipData,
  SmartFilterI,
} from '../../../ContentfulTypes';
import { phonesCountSelector } from '../../../redux/selectors/phoneSelectors';

import createFilterState from './createFilterState';
import { createFilterStateFromURL } from './createFilterStateFromURL';
import updatePhoneFilter from './updatePhoneFilter';
import PhoneFilters from './PhoneFilters/PhoneFilters';
import {
  Container,
  PhoneColumnWrapper,
  PhobioMsaContainer,
  PhobioContainer,
  PageWrapper,
  SmartFilters,
} from './styles';

const FeatureBanner = React.lazy(() => import('../../UI/Banners/FeatureBanner/FeatureBanner'));

const basePath = 'phones';

interface Props {
  locale: string;
  preferredProvince?: string;
  metaTitle?: string;
  metaDescription?: string;
  location: Record<string, any>;
  contentfulData: Record<string, any>;
  allPhonesData: Record<string, any>;
  phoneHeading: string;
  tabletHeading: string;
  prepaidPhonesHeading: string;
  phoneBrands: Record<string, any>;
}

const PhoneScreen = ({
  locale,
  preferredProvince: province = '',
  location,
  metaDescription,
  metaTitle,
  contentfulData,
  allPhonesData,
  phoneHeading,
  tabletHeading,
  prepaidPhonesHeading,
  phoneBrands,
}: Props) => {
  const [filtersActivated, setFiltersActivated] = useState(false);
  const [bladeVisible, setBladeVisible] = useState(false);
  const dispatch = useDispatch();
  const storedFilters = useSelector(filtersSelector);
  const currentProvince = useSelector(provinceSelector);
  const phonesCount = useSelector(phonesCountSelector);
  const isKioskMode = isKiosk();
  const { search } = useLocationRouter();
  // Hero Banner
  const featureBannerData = useMemo(() => extractArrayData(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    '2tyPMYsNYqij8BnOuJMZde',
  ), [contentfulData.pageSections]);

  // Mobilist Tip Data
  const mobilistTipsData = extractArrayData(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    '5stChHJY8pHGSWljR2mhiO',
  )?.combinatorSectionItems as Array<MobilistTipData>;

  const conditionalBannerTopMulti = extractArrayData<ContentfulConditionalSection>(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    '6xfo5q6cDG5i3vnkm83cRe',
  );

  const conditionalBannerBottomMulti = extractArrayData<ContentfulConditionalSection>(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    'h09LWUq7K2nNNvdyDXyh4',
  );

  // LEGAL DETAILS DATA ----------------------------------------------
  const multiHeroBannerLegalTextTop = featureBannerData
    ?.combinatorSectionItems?.[0]?.bannerLegalText;
  const multiHeroBannerLegalTextBottom = (
    conditionalBannerBottomMulti?.sectionContent?.bannerLegalText
  );
  const legalText = [multiHeroBannerLegalTextTop, multiHeroBannerLegalTextBottom];
  const legalTextKiosk = [multiHeroBannerLegalTextTop, multiHeroBannerLegalTextBottom];
  
  const {
    contentBody: legalTextSection,
    heading: legalTextHeading,
  } = useMemo(() => extractArrayData(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    '5TSNsnpJF4j08xhL5S1SDS',
  ), [contentfulData.pageSections]);
      
  const {
    contentBody: legalTextSectionKiosk,
    heading: legalTextHeadingKiosk,
  } = useMemo(() => extractArrayData(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    'uSIOqDPKyUqRAsGALcpGn',
  ), [contentfulData.pageSections]);
    
  // Add the other page legal text to the hero banners legal
  legalText?.push(legalTextSection);
  legalTextKiosk?.push(legalTextSectionKiosk);

  // Labels for phone components
  const phoneLabelData = extractArrayData<ContentfulForm>(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    '7fWwxbJyI7B6fIBnxJDnOB',
  );

  // Phone page no results data
  const phonePageNoResultsData = extractArrayData<ContentfulCombinatorSection>(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    '2aQogLExnyDfIB1yH80uCN',
  );

  // Smart filters section
  const smartFiltersData = extractArrayData(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    '1a1UVTunVlqWMYlCveK8B3',
  ).filterGroups as SmartFilterI[];

  // MSA section
  const msaData = extractArrayData(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    '2lCmwx9IFSaT0UomSTOJNc',
  );

  // Phobio section
  const phobioData = extractArrayData(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    '5WUtBHJGymhReE1Tai5Plq',
  );

  const { filterLabels, filterBlade } = extractArrayData(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    '1a1UVTunVlqWMYlCveK8B3',
  ) as SmartFilterI;

  const { response: tradeInPhones, loading: tradeInLoading } = useGetTradeInPhones(
    { minTradeInValue: 1 },
  );

  // Create state object to handle filter activiy, made from CMS values
  const defaultFilters = useMemo(() => {
    const filterState = {
      standard: createFilterState({
        filterCategories: smartFiltersData[0].filterCategories,
        isStandard: true,
        province,
      }),
      advanced: createFilterState({
        filterCategories: smartFiltersData[1].filterCategories,
        isStandard: false,
        province,
      }),
    };
    return filterState;
  }, [province, smartFiltersData]);

  // Reset filters - used on MSA initiation
  const resetFilters = (startOver) => {
    dispatch(updateStoredFilters(defaultFilters));
    navigateToURLWithQueryParams({
      locale,
      basePath,
      params: new URLSearchParams(),
    });
    if (startOver !== 'startOver') {
      setIsMsaActive(false);
    }
  };

  // If MSA is initiated, hide total phone count in <Products />
  const [isMsaActive, setIsMsaActive] = useState(false);
  const [isPhobioActive, setIsPhobioActive] = useState(false);

  // Change handler called when a filter checkbox is clicked
  // Pass in filter specs, and optional selectAll value (boolean to toggle all options in category)
  const activeFilterUpdate = useMemo(
    () => (category, item) => {
      updatePhoneFilter({ category, item, search, locale });
    },
    [search, locale],
  );

  const supportedPhoneURLParams = [
    { param: 'brand', name: 'Brand', id: 'standard-brand', reduxIndex: 0 },
    { param: 'carrier', name: 'Carrier', id: 'standard-carrier', reduxIndex: 1 },
    { param: 'offers', name: 'Offers', id: 'standard-offers', reduxIndex: 2 },
  ];

  const onFilterChange = (filter, option) => {
    const params = new URLSearchParams(search.replace(/\/$/, ''));

    // Find URL param for the selected filter
    const parameterName = supportedPhoneURLParams.find(
      (param) => param.id === filter.id,
    );
    const value = option.id.replace(/\s+/g, '-');
    const existingParameter = params.get(parameterName?.param as string)?.split(' ');
    if (existingParameter != null) {
      // Already have some value for this param
      // Check if value is there
      if (existingParameter.includes(value)) {
        const updated = existingParameter.filter((p) => p !== value).join(' ');
        if (updated === '') {
          params.delete(parameterName?.param as string);
        } else {
          params.set(parameterName?.param as string, updated);
        }
      } else {
        existingParameter.push(value);
        params.set(parameterName?.param as string, existingParameter.join(' '));
      }
    } else {
      // New value for this parameter
      params.append(parameterName?.param as string, value.replace(/\s+/g, '-'));
    }
    navigateToURLWithQueryParams({ locale, basePath, params });
  };

  // When PhoneScreen mounts, pull storedFilters from redux if it exists
  // Also set the current province to redux to compare against later
  useEffect(() => {
    dispatch(updateCurrentProvince(province));
  }, [dispatch, province]);

  // When province updates, check it against redux state
  // If it's different, user has changed provinces, and we rerun the state creation based on new province
  // If it's the same, user has simply navigated pages and we should not reset the filter state
  useEffect(() => {
    if (currentProvince !== province) {
      dispatch(updateCurrentProvince(province));
      dispatch(updateStoredFilters(defaultFilters));
    }
  }, [currentProvince, defaultFilters, dispatch, province]);

  useEffect(() => {
    const params = new URLSearchParams(search.replace(/\/$/, ''));
    const newFilters = createFilterStateFromURL(params, storedFilters, defaultFilters);
    
    if (!isEqual(newFilters, storedFilters)) {
      dispatch(
        updateStoredFilters(newFilters),
      );
    }
    setFiltersActivated(params.size > 0);
  }, [dispatch, search, defaultFilters, storedFilters]);
  // If the user gets to brand or carrier specific phones page (ex "phones/apple" URL) pre toggle filter for it
  useEffect(
    () => () => {
      dispatch(updateStoredFilters({}));
    },
    [dispatch],
  );

  // Make sure Phobio is closed if this page is launching with MSA open
  useEffect(() => {
    if (location?.state?.MSAPhase) {
      setIsPhobioActive(false);
      setIsMsaActive(true);
    }
  }, [location.state]);

  const { homePage, currentPage } = useLocation();
  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: homePage,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Phones',
        item: currentPage,
      },
    ],
  };

  return (
    <PageWrapper>
 
      {metaTitle && (
        <Seo title={metaTitle} description={metaDescription} ldJson={schema} />
      )}

      {/* Hero Banner */}
      {featureBannerData && (
        <Suspense>
          <FeatureBanner data={featureBannerData} index={0} />
        </Suspense>
      )}

      <Container>
        <PhobioMsaContainer $active={isMsaActive || isPhobioActive}>
          {/* Phobio */}
          <PhobioContainer hide={isMsaActive}>
            <Phobio
              phobioData={{ ...phobioData, phoneBrands }}
              setActive={setIsPhobioActive}
              phonePage
              tradeInPhones={tradeInPhones}
              tradeInLoading={tradeInLoading}
            />
          </PhobioContainer>
          {/* MSA Filter */}
          <PhobioContainer hide={isPhobioActive}>
            <MSAFilter
              msaData={msaData}
              activeFilterUpdate={activeFilterUpdate}
              resetFilters={resetFilters}
              province={province}
              isMsaActive={isMsaActive}
              setIsMsaActive={setIsMsaActive}
              location={location}
              allPhones={allPhonesData.phones}
              locale={locale}
            />
          </PhobioContainer>
        </PhobioMsaContainer>

        <SmartFilters>
          <PhoneFilters
            filterObject={smartFiltersData}
            onFilterChange={onFilterChange}
            resetFilters={resetFilters}
            storedFilters={storedFilters.standard}
            bladeVisible={bladeVisible}
            setBladeVisible={setBladeVisible}
            filtersActivated={filtersActivated}
            phonesCount={phonesCount}
            filterLabels={filterLabels}
            filterBlade={filterBlade}
          />
        </SmartFilters>

        <PhoneColumnWrapper>
          <Products
            products={allPhonesData.phones}
            mobilistTipsData={mobilistTipsData}
            province={province}
            isMsaActive={isMsaActive}
            location={location as WindowLocation<unknown>}
            standardNoResults={phonePageNoResultsData as ContentfulCombinatorSection}
            phoneLabelData={phoneLabelData as ContentfulForm}
            phoneHeading={phoneHeading}
            tabletHeading={tabletHeading}
            prepaidPhonesHeading={prepaidPhonesHeading}
            conditionalMultiBannerTop={conditionalBannerTopMulti}
            conditionalMultiBannerBottom={conditionalBannerBottomMulti}
          />
        </PhoneColumnWrapper>
        <LegalText
          preExpand
          legalText={isKioskMode ? legalTextKiosk : legalText}
          legalHeading={isKioskMode ? legalTextHeadingKiosk : legalTextHeading}
          pageId="phones"
        />
      </Container>
    </PageWrapper>
  );
};

export default PhoneScreen;
